import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import React, { useEffect, useMemo } from "react";
import {
  CVRG_CD_JEDI_DROPDOWN_ITEM,
  INITIAL_SPREADSHEET_ROW,
  SpreadsheetState,
  usePageState,
  UserRoleInfoKeys,
  UserRoleInfoRow,
} from ".";
import { useAppState } from "../../app/useAppState";

import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { UserRoleMaintenancePageInitialState } from ".";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {} from "../../services/data-kinds";
import {
  useGetDips,
  useGetGeneralCodes,
  useGetRoleCodeList,
} from "../../services/data-list";
import {} from "../../services/deployment-request";
import {
  InDefCommonFlagType,
  RoleClssfctnType,
  RoleType,
} from "../../util/general-code/enums/general-codes";
import {
  ErrorResponse,
  INIT_ERROR_RESULT_DATA,
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import {
  CreateUserRolesRequest,
  useCreateUserRoles,
  useDeleteUserRoles,
  useGetUserRoles,
  UserRoleInfo,
} from "../../services/user-roles";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const UserRoleMaintenancePageContent = () => {
  const { showToast, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();

  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();

  // API Clients
  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getRoleCodeListResponse, sendGetRoleCodeList] = useGetRoleCodeList();
  const [getDipsReponse, sendGetDips] = useGetDips();
  const [getUserRolesResponse, sendGetUserRolesRequest] = useGetUserRoles();
  const [createUserRoleResponse, sendCreateUserRoleRequest] =
    useCreateUserRoles();
  const [deleteUserRoleResponse, sendDeleteUserRoleRequest] =
    useDeleteUserRoles();

  const TEMPLATE_FILE_URL = "spreadsheet-template/user-role-maintenance.xlsx";

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: "User Role Maintenance",
    }));
    sendGetGeneralCodesRequest({ cdTypes: ["in_def_common_flag_type"] });
    sendGetUserRolesRequest({});
    sendGetRoleCodeList({ roleClssfcnCd: RoleClssfctnType.DIPROLE });
    sendGetDips({});
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === "in_def_common_flag_type",
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inDefCommonFlagType,
      },
    }));
  };

  const onGetRoleCodeListResponse = () => {
    if (!getRoleCodeListResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        dipRoleList: getRoleCodeListResponse.roleCodeList.filter(
          (e) => e.value !== RoleType.SYSTEM,
        ),
      },
    }));
  };

  const onGetDipsResponse = () => {
    if (!getDipsReponse) return;

    const cvrgCdList = getDipsReponse.dipList.map((dip) => ({
      label: dip.dip,
      value: dip.dip,
    }));
    cvrgCdList.push(CVRG_CD_JEDI_DROPDOWN_ITEM);

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        cvrgCdList: cvrgCdList,
      },
    }));
  };

  const onGetUserRoleResponded = () => {
    if (!getUserRolesResponse) {
      return;
    }
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        getUserRolesResp: getUserRolesResponse,
      },
    }));
  };

  const onCreateUserRoleResponded = () => {
    if (!createUserRoleResponse) return;
    if (Array.isArray(createUserRoleResponse)) {
      const currentrecords: UserRoleInfoRow[] = spreadsheetState
        ? [...spreadsheetState.userRoleInfoList]
        : [];

      const currentrecordsWithoutBackendError = currentrecords.map((row) =>
        clearBackenddMessages(row),
      );
      const newSpreadsheetRecordData: UserRoleInfoRow[] =
        mappingErrorResponseToSpreadsheet(
          createUserRoleResponse,
          currentrecordsWithoutBackendError,
          UserRoleMaintenancePageInitialState.displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        userRoleInfoList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetUserRolesRequest({});
    }
  };

  const onDeleteUserRoleResponded = () => {
    if (!deleteUserRoleResponse) return;
    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    sendGetUserRolesRequest({});
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetRoleCodeListResponse, [getRoleCodeListResponse]);
  useEffect(onGetDipsResponse, [getDipsReponse]);
  useEffect(onGetUserRoleResponded, [getUserRolesResponse]);
  useEffect(onCreateUserRoleResponded, [createUserRoleResponse]);
  useEffect(onDeleteUserRoleResponded, [deleteUserRoleResponse]);

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData = useMemo(() => {
    if (!pageState.searchResult.getUserRolesResp || !pageState.dataList) return;

    return {
      userRoleInfoList: pageState.searchResult.getUserRolesResp.userRoles.map(
        (e) => ({
          ...INIT_ERROR_RESULT_DATA,
          userRoleInfo: e,
        }),
      ),
      dataList: pageState.dataList,
    };
  }, [pageState.searchResult, pageState.dataList]);

  const onEditSpreadsheet = (event: CustomEvent) => {
    if (!spreadsheetState?.dataList) return;

    const editedRowIdx: number = event.detail.cell.data.index;
    const newValue: string = event.detail.cell.value ?? "";
    const eventName: string = event.detail.name;
    const newSpreadSheetRows: UserRoleInfoRow[] = JSON.parse(
      JSON.stringify(spreadsheetState?.userRoleInfoList),
    );

    if (eventName === "delCheckBox") {
      if (newSpreadSheetRows[editedRowIdx].addFlg === InDefCommonFlagType.YES)
        return;
      newSpreadSheetRows[editedRowIdx].delCheckBox =
        !newSpreadSheetRows[editedRowIdx].delCheckBox;
    } else {
      newSpreadSheetRows[editedRowIdx] = {
        ...spreadsheetState.userRoleInfoList[editedRowIdx],
        userRoleInfo: {
          ...spreadsheetState.userRoleInfoList[editedRowIdx].userRoleInfo,
          [eventName]: newValue,
        },
      };
    }
    setSpreadsheetState({
      ...spreadsheetState,
      userRoleInfoList: newSpreadSheetRows,
    });
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newUserRoleList = _.cloneDeep(
      spreadsheetState?.userRoleInfoList,
    ) as UserRoleInfoRow[];

    const colName: UserRoleInfoKeys = event.detail.name as UserRoleInfoKeys;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      newUserRoleList[rowIdx].userRoleInfo[colName] = value;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      userRoleInfoList: newUserRoleList,
    });
  };

  /**
   * Add Row Button Clicked.
   * @param event that is passed from spreadsheet.
   */
  const onAddRowClicked = () => {
    if (!spreadsheetState) return;
    const insertRowIndex: number =
      (pageState.spreadsheetSelectedRowIndex ?? 0) + 1;
    const newSpreadsheetState = spreadsheetState
      ? [...spreadsheetState.userRoleInfoList]
      : [];
    const addSpreadsheetRow: UserRoleInfoRow = { ...INITIAL_SPREADSHEET_ROW };
    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      userRoleInfoList: [...newSpreadsheetState],
    });
  };

  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheetSelectedRowIndex: rowIndex,
    }));
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    if (event.detail.name !== "deleteIcon") return;
    const newSpreadsheetState: UserRoleInfoRow[] = spreadsheetState
      ? [...spreadsheetState.userRoleInfoList]
      : [];
    const rowIndex: number = event.detail.cell?.data.index;
    newSpreadsheetState.splice(rowIndex, 1);
    setSpreadsheetState({
      ...spreadsheetState,
      userRoleInfoList: newSpreadsheetState,
    });
    setPageState((prevState) => ({
      ...prevState,
    }));
  };
  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    const onSaveClicked = () => {
      if (!spreadsheetState) return;
      const errorList = spreadsheetState?.userRoleInfoList
        .filter((e) => e.addFlg === InDefCommonFlagType.YES)
        .map((e) => {
          const error: ErrorResponse = {
            index: spreadsheetState?.userRoleInfoList.indexOf(e),
            errorColumnList: [],
            messageList: [],
          };
          if (e.userRoleInfo.userId === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "userId",
            });
            error.errorColumnList.push("userId");
          }
          if (e.userRoleInfo.roleCd === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "roleCd",
            });
            error.errorColumnList.push("roleCd");
          }
          if (e.userRoleInfo.cvrgCd === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "cvrgCd",
            });
            error.errorColumnList.push("cvrgCd");
          }
          if (e.userRoleInfo.primFlg === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "primFlg",
            });
            error.errorColumnList.push("primFlg");
          }
          return error;
        })
        .filter((e) => e.messageList.length > 0);

      if (errorList && errorList.length > 0) {
        const currentSpreadsheetRow: UserRoleInfoRow[] = spreadsheetState
          ? [...spreadsheetState.userRoleInfoList]
          : [];
        const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
          errorList,
          currentSpreadsheetRow,
          UserRoleMaintenancePageInitialState.displayColumn,
        );
        setSpreadsheetState({
          ...spreadsheetState,
          userRoleInfoList: [...newSpreadsheetRow],
        });
        return;
      }

      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          const createUserRolesRequest = spreadsheetState?.userRoleInfoList
            .map((e, idx) => ({
              ...e,
              idx: idx,
            }))
            .filter((e) => e.addFlg === InDefCommonFlagType.YES)
            .map((e) => ({
              ...e.userRoleInfo,
              idx: e.idx,
            }));
          if (!createUserRolesRequest) return;
          sendCreateUserRoleRequest({ userRoles: createUserRolesRequest });
        },
      });
    };

    const onDeleteClicked = () => {
      if (!spreadsheetState) return;
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          const deleteUserRolesRequest = spreadsheetState?.userRoleInfoList
            .map((e, idx) => ({
              ...e,
              idx: idx,
            }))
            .filter((e) => e.delCheckBox)
            .map((e) => ({
              ...e.userRoleInfo,
              idx: e.idx,
            }));
          if (!deleteUserRolesRequest) return;
          sendDeleteUserRoleRequest({ userRoles: deleteUserRolesRequest });
        },
      });
    };

    return (
      <FrVrfFooter>
        {/* Save Button */}
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          disabled={
            !spreadsheetState?.userRoleInfoList.some(
              (e) => e.addFlg === InDefCommonFlagType.YES,
            )
          }
          onClicked={onSaveClicked}
          authActId="user_role_maintenance.button.save"
        />
        <FrVrfButton
          content="Delete"
          theme="primary"
          size="large"
          disabled={
            !spreadsheetState?.userRoleInfoList.some((e) => e.delCheckBox)
          }
          onClicked={onDeleteClicked}
          authActId="user_role_maintenance.button.delete"
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      <FrVrfTextDisplayField text="User Role Maintenance" />
      <FrVrfButtonPanel>
        <FrVrfButton
          content="Add Row"
          size="medium"
          theme="secondary"
          slot="left"
          onClicked={onAddRowClicked}
          authActId="user_role_maintenance.button.add_row"
        />
      </FrVrfButtonPanel>
      <br />
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_URL}
        onInitialized={initSpreadsheetState}
        onCellSelected={onCellSelected}
        onCellClicked={onDeleteIconClicked}
        onCellEdited={onEditSpreadsheet}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />

      {/* Footer */}
      <FooterContent />
    </>
  );
};
